import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet } from '../api/client';
import { formatPhone } from '../utils/formatPhone';
import type {
  VaultResponse,
  ProfileListResponse,
  ProfileResponse,
  EmergencyContactListResponse,
  EmergencyContactResponse,
  DocumentListResponse,
  DocumentResponse,
  HeirloomListResponse,
  HeirloomResponse,
  SecureNoteListResponse,
  SecureNoteResponse,
  FuneralPlanListResponse,
  FuneralPlanResponse,
  FinancialAccountListResponse,
  FinancialAccountResponse,
  LetterListResponse,
  LetterResponse,
  VideoListResponse,
  VideoResponse,
} from '../types/api';
import { VaultRole, DocumentCategory, FinancialAccountType, LetterState, VideoState } from '../types/api';
import LoadingSpinner from '../components/LoadingSpinner';
import './PrintVaultPage.css';

const roleLabels: Record<number, string> = {
  [VaultRole.Owner]: 'Owner',
  [VaultRole.Spouse]: 'Spouse',
  [VaultRole.Witness]: 'Witness',
};

const docCategoryLabels: Record<number, string> = {
  [DocumentCategory.Will]: 'Will',
  [DocumentCategory.Trust]: 'Trust',
  [DocumentCategory.Insurance]: 'Insurance',
  [DocumentCategory.Medical]: 'Medical',
  [DocumentCategory.Financial]: 'Financial',
  [DocumentCategory.Photo]: 'Photo',
  [DocumentCategory.Video]: 'Video',
  [DocumentCategory.Other]: 'Other',
  [DocumentCategory.Deed]: 'Deed',
  [DocumentCategory.PowerOfAttorney]: 'Power of Attorney',
  [DocumentCategory.BirthCertificate]: 'Birth Certificate',
  [DocumentCategory.MarriageCertificate]: 'Marriage Certificate',
  [DocumentCategory.DeathCertificate]: 'Death Certificate',
  [DocumentCategory.SocialSecurity]: 'Social Security',
  [DocumentCategory.TaxRecord]: 'Tax Record',
  [DocumentCategory.FuneralInstructions]: 'Funeral Instructions',
  [DocumentCategory.PersonalLetter]: 'Personal Letter',
  [DocumentCategory.Membership]: 'Membership',
};

const financialTypeLabels: Record<number, string> = {
  [FinancialAccountType.Checking]:      'Checking',
  [FinancialAccountType.Savings]:       'Savings',
  [FinancialAccountType.Brokerage]:     'Brokerage',
  [FinancialAccountType.IRA]:           'IRA',
  [FinancialAccountType.RothIRA]:       'Roth IRA',
  [FinancialAccountType.FourOhOneK]:    '401(k)',
  [FinancialAccountType.Pension]:       'Pension',
  [FinancialAccountType.LifeInsurance]: 'Life Insurance',
  [FinancialAccountType.CreditCard]:    'Credit Card',
  [FinancialAccountType.Mortgage]:      'Mortgage',
  [FinancialAccountType.Other]:         'Other',
  [FinancialAccountType.OnlineAccount]:  'Online Account',
};


function formatDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString();
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

export default function PrintVaultPage() {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [vault, setVault] = useState<VaultResponse | null>(null);
  const [profiles, setProfiles] = useState<ProfileResponse[]>([]);
  const [contacts, setContacts] = useState<EmergencyContactResponse[]>([]);
  const [documents, setDocuments] = useState<DocumentResponse[]>([]);
  const [heirlooms, setHeirlooms] = useState<HeirloomResponse[]>([]);
  const [notes, setNotes] = useState<SecureNoteResponse[]>([]);
  const [funeralPlans, setFuneralPlans] = useState<FuneralPlanResponse[]>([]);
  const [financialAccounts, setFinancialAccounts] = useState<FinancialAccountResponse[]>([]);
  const [letters, setLetters] = useState<LetterResponse[]>([]);
  const [videos, setVideos] = useState<VideoResponse[]>([]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const [vaultData, profileData, contactData, docData, heirloomData, noteListData, planListData, financialData, letterData, videoData] =
          await Promise.all([
            apiGet<VaultResponse>(`/api/vault/${id}`),
            apiGet<ProfileListResponse>(`/api/vault/${id}/profiles`),
            apiGet<EmergencyContactListResponse>(`/api/vault/${id}/emergency-contacts`),
            apiGet<DocumentListResponse>(`/api/vault/${id}/documents`),
            apiGet<HeirloomListResponse>(`/api/vault/${id}/heirlooms`),
            apiGet<SecureNoteListResponse>(`/api/vault/${id}/notes`),
            apiGet<FuneralPlanListResponse>(`/api/vault/${id}/funeral-plans`),
            apiGet<FinancialAccountListResponse>(`/api/vault/${id}/financial-accounts`),
            apiGet<LetterListResponse>(`/api/vault/${id}/letters`),
            apiGet<VideoListResponse>(`/api/vault/${id}/legacy-videos`),
          ]);

        setVault(vaultData);
        setProfiles(profileData.profiles);
        setContacts(contactData.contacts);
        setDocuments(docData.documents);
        setHeirlooms(heirloomData.heirlooms);
        setFinancialAccounts(financialData.accounts);
        setLetters(letterData.letters);
        setVideos(videoData.videos);

        // Fetch full content for each note
        const fullNotes = await Promise.all(
          noteListData.notes.map(n =>
            apiGet<SecureNoteResponse>(`/api/vault/${id}/notes/${n.id}`)
          )
        );
        setNotes(fullNotes);

        // Fetch full content for each funeral plan
        const fullPlans = await Promise.all(
          planListData.plans.map(p =>
            apiGet<FuneralPlanResponse>(`/api/vault/${id}/funeral-plans/${p.id}`)
          )
        );
        setFuneralPlans(fullPlans);
      } catch (err: any) {
        setError(err.message || 'Failed to load vault data.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const printDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  if (loading) return <LoadingSpinner fullPage />;

  if (error) {
    return (
      <div className="print-vault-page">
        <p className="print-vault-error">{error}</p>
        <Link to="/">&larr; Back to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="print-vault-page">
      <div className="print-vault-actions no-print">
        <button className="btn-primary" onClick={() => window.print()}>
          Print / Save as PDF
        </button>
        <Link to="/" className="btn-text">&larr; Back to Vault</Link>
      </div>

      <div className="print-vault-content">
        <header className="print-vault-header">
          <h1>Cedar Vault&#174; &mdash; {vault?.name}</h1>
          <p className="print-vault-date">Printed {printDate}</p>
        </header>

        {/* ── Member Profiles ── */}
        {profiles.length > 0 && (
          <section className="print-section">
            <h2>Member Profiles</h2>
            {profiles.map(p => (
              <div key={p.membershipId} className="print-card">
                <h3>{p.preferredName || `${p.firstName} ${p.lastName}`}
                  <span className="print-role">{roleLabels[p.role] || 'Member'}</span>
                </h3>
                {p.fullLegalName && <PrintRow label="Full Legal Name" value={p.fullLegalName} />}
                {p.dateOfBirth && <PrintRow label="Date of Birth" value={formatDate(p.dateOfBirth)} />}
                {p.phoneNumber && <PrintRow label="Phone" value={formatPhone(p.phoneNumber)} />}
                {(p.addressStreet || p.addressCity) && (
                  <PrintRow label="Address" value={[
                    p.addressStreet,
                    [p.addressCity, p.addressState].filter(Boolean).join(', '),
                    p.addressZip,
                  ].filter(Boolean).join('\n')} />
                )}
                {p.notes && <PrintRow label="Notes" value={p.notes} />}
              </div>
            ))}
          </section>
        )}

        {/* ── Emergency Contacts ── */}
        {contacts.length > 0 && (
          <section className="print-section">
            <h2>Emergency Contacts</h2>
            {contacts.map(c => (
              <div key={c.id} className="print-card">
                <h3>{c.name} <span className="print-role">{c.relationship}</span></h3>
                {c.phone && <PrintRow label="Phone" value={formatPhone(c.phone)} />}
                {c.email && <PrintRow label="Email" value={c.email} />}
                {c.company && <PrintRow label="Company" value={c.company} />}
                {c.notes && <PrintRow label="Notes" value={c.notes} />}
              </div>
            ))}
          </section>
        )}

        {/* ── Documents ── */}
        {documents.length > 0 && (
          <section className="print-section">
            <h2>Documents</h2>
            <table className="print-table">
              <thead>
                <tr>
                  <th>File Name</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(d => (
                  <tr key={d.id}>
                    <td>{d.fileName}</td>
                    <td>{docCategoryLabels[d.category] || 'Other'}</td>
                    <td>{formatBytes(d.fileSizeBytes)}</td>
                    <td>{formatDate(d.uploadedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* ── Financial Accounts ── */}
        {financialAccounts.length > 0 && (
          <section className="print-section">
            <h2>Important Accounts</h2>
            <table className="print-table">
              <thead>
                <tr>
                  <th>Institution</th>
                  <th>Type</th>
                  <th>Account #</th>
                  <th>Phone</th>
                  <th>Website</th>
                </tr>
              </thead>
              <tbody>
                {financialAccounts.map(fa => (
                  <tr key={fa.id}>
                    <td>{fa.institutionName}</td>
                    <td>{financialTypeLabels[fa.accountType] || 'Other'}</td>
                    <td>{fa.accountNumber || ''}</td>
                    <td>{fa.contactPhone ? formatPhone(fa.contactPhone) : ''}</td>
                    <td>{fa.website || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {financialAccounts.some(fa => fa.notes) && (
              <>
                <h3 style={{ marginTop: '12px', fontSize: '0.95rem' }}>Account Notes</h3>
                {financialAccounts.filter(fa => fa.notes).map(fa => (
                  <div key={fa.id} className="print-card">
                    <h3>{fa.institutionName} <span className="print-role">{financialTypeLabels[fa.accountType] || 'Other'}</span></h3>
                    <PrintRow label="Notes" value={fa.notes!} />
                  </div>
                ))}
              </>
            )}
          </section>
        )}

        {/* ── Heirlooms ── */}
        {heirlooms.length > 0 && (
          <section className="print-section">
            <h2>Possessions</h2>
            {heirlooms.map(h => (
              <div key={h.id} className="print-card">
                <h3>{h.title}</h3>
                <PrintRow label="Instructions" value={h.instructions} />
              </div>
            ))}
          </section>
        )}

        {/* ── Private Notes ── */}
        {notes.length > 0 && (
          <section className="print-section">
            <h2>Private Notes</h2>
            {notes.map(n => (
              <div key={n.id} className="print-card">
                <h3>{n.title}</h3>
                {n.content && <PrintRow label="Content" value={n.content} />}
              </div>
            ))}
          </section>
        )}

        {/* ── Funeral Plans ── */}
        {funeralPlans.length > 0 && (
          <section className="print-section">
            <h2>Funeral Plans</h2>
            {funeralPlans.map(fp => (
              <div key={fp.id} className="print-card">
                <h3>{fp.personName}</h3>
                {fp.overallWishes && <PrintRow label="Overall Wishes" value={fp.overallWishes} />}
                {fp.serviceType && <PrintRow label="Service Type" value={fp.serviceType} />}
                {fp.serviceLocation && <PrintRow label="Service Location" value={fp.serviceLocation} />}
                {fp.officiant && <PrintRow label="Officiant" value={fp.officiant} />}
                {fp.musicSelections && <PrintRow label="Music" value={fp.musicSelections} />}
                {fp.scriptureReadings && <PrintRow label="Scripture Readings" value={fp.scriptureReadings} />}
                {fp.readings && <PrintRow label="Other Readings" value={fp.readings} />}
                {fp.eulogyBy && <PrintRow label="Eulogy By" value={fp.eulogyBy} />}
                {fp.pallbearers && <PrintRow label="Pallbearers" value={fp.pallbearers} />}
                {fp.specialTributes && <PrintRow label="Special Tributes" value={fp.specialTributes} />}
                {fp.dispositionType && <PrintRow label="Disposition" value={fp.dispositionType} />}
                {fp.cemeteryName && <PrintRow label="Cemetery" value={fp.cemeteryName} />}
                {fp.cemeteryLocation && <PrintRow label="Cemetery Location" value={fp.cemeteryLocation} />}
                {fp.plotInformation && <PrintRow label="Plot Info" value={fp.plotInformation} />}
                {fp.casketPreference && <PrintRow label="Casket" value={fp.casketPreference} />}
                {fp.vaultPreference && <PrintRow label="Burial Vault" value={fp.vaultPreference} />}
                {fp.openClosedCasket && <PrintRow label="Open/Closed" value={fp.openClosedCasket} />}
                {fp.urnPreference && <PrintRow label="Urn" value={fp.urnPreference} />}
                {fp.remainsDisposition && <PrintRow label="Remains" value={fp.remainsDisposition} />}
                {fp.viewingWishes && <PrintRow label="Viewing" value={fp.viewingWishes} />}
                {fp.viewingNotes && <PrintRow label="Viewing Notes" value={fp.viewingNotes} />}
                {fp.clothingPreference && <PrintRow label="Clothing" value={fp.clothingPreference} />}
                {fp.personalItems && <PrintRow label="Personal Items" value={fp.personalItems} />}
                {fp.flowerPreference && <PrintRow label="Flowers" value={fp.flowerPreference} />}
                {fp.donationOrganizations && <PrintRow label="Donations" value={fp.donationOrganizations} />}
                {fp.obituaryWishes && <PrintRow label="Obituary" value={fp.obituaryWishes} />}
                {fp.receptionWishes && <PrintRow label="Reception" value={fp.receptionWishes} />}
                {fp.funeralHome && <PrintRow label="Funeral Home" value={fp.funeralHome} />}
                {fp.funeralHomePhone && <PrintRow label="Funeral Home Phone" value={fp.funeralHomePhone} />}
                {fp.prePaidArrangements && <PrintRow label="Pre-Paid" value={fp.prePaidArrangements} />}
                {fp.veteranInfo && <PrintRow label="Veteran Info" value={fp.veteranInfo} />}
                {fp.organDonation && <PrintRow label="Organ Donation" value={fp.organDonation} />}
                {fp.additionalWishes && <PrintRow label="Additional Wishes" value={fp.additionalWishes} />}
              </div>
            ))}
          </section>
        )}

        {/* ── Legacy Letters ── */}
        {letters.length > 0 && (
          <section className="print-section">
            <h2>Legacy Letters</h2>
            {letters.map(l => {
              const stateLabel = l.state === LetterState.Unsealed ? 'Unsealed'
                               : l.state === LetterState.Sealed   ? 'Sealed'
                               :                                    'Draft';
              return (
                <div key={l.id} className="print-card">
                  <h3>
                    To: {l.addressedTo}
                    <span className="print-role">{stateLabel}</span>
                  </h3>
                  <PrintRow label="Subject" value={l.subject} />
                  {l.state === LetterState.Sealed && (
                    <PrintRow label="Note" value="This letter is sealed and will open when the family votes to unseal it." />
                  )}
                  {l.body && l.state !== LetterState.Sealed && (
                    <PrintRow label="Letter" value={l.body} />
                  )}
                  {l.sealedAt && l.state === LetterState.Sealed && (
                    <PrintRow label="Sealed" value={formatDate(l.sealedAt)} />
                  )}
                  {l.unsealedAt && l.state === LetterState.Unsealed && (
                    <PrintRow label="Unsealed" value={formatDate(l.unsealedAt)} />
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* ── Legacy Video Messages ── */}
        {videos.length > 0 && (
          <section className="print-section">
            <h2>Legacy Video Messages</h2>
            <p className="print-video-note">
              Video files cannot be included in a printed summary. Access them in your Cedar Vault.
            </p>
            {videos.map(v => {
              const stateLabel = v.state === VideoState.Unsealed ? 'Unsealed'
                               : v.state === VideoState.Sealed   ? 'Sealed'
                               :                                   'Draft';
              return (
                <div key={v.id} className="print-card">
                  <h3>
                    {v.title}
                    <span className="print-role">{stateLabel}</span>
                  </h3>
                  {v.description && <PrintRow label="Description" value={v.description} />}
                  <PrintRow label="File" value={`${v.originalFileName} (${formatBytes(v.fileSizeBytes)})`} />
                  {v.sealedAt && v.state === VideoState.Sealed && (
                    <PrintRow label="Sealed" value={formatDate(v.sealedAt)} />
                  )}
                  {v.unsealedAt && v.state === VideoState.Unsealed && (
                    <PrintRow label="Unsealed" value={formatDate(v.unsealedAt)} />
                  )}
                  {v.createdByName && (
                    <PrintRow label="Recorded by" value={v.createdByName} />
                  )}
                </div>
              );
            })}
          </section>
        )}

        <footer className="print-vault-footer">
          Confidential &mdash; Printed from Cedar Vault on {printDate}
        </footer>
      </div>
    </div>
  );
}

function PrintRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="print-row">
      <span className="print-row-label">{label}</span>
      <span className="print-row-value">{value}</span>
    </div>
  );
}
